import type React from "react";
import { useMemo } from "react";

import { faPhone, faVideo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { AppointmentV1 } from "api/services/appointment/schemas/appointment_v1";
import { BLUE } from "theme/colors";
import { useLocalizedDate } from "utils/date";

interface Props {
  appointmentsV1: AppointmentV1[];
  chosenDate: Date;
}

export const DaySchedule: React.FC<Props> = ({ appointmentsV1, chosenDate }) => {
  const { t } = useTranslation();
  const { format, isSameDay } = useLocalizedDate();

  const calls = useMemo(
    () =>
      appointmentsV1
        .filter((call: AppointmentV1) => isSameDay(parseISO(call.start_time), chosenDate))
        .sort((a: AppointmentV1, b: AppointmentV1) => (a.start_time < b.start_time ? -1 : 1)),
    [appointmentsV1, chosenDate]
  );

  if (calls.length === 0) {
    return (
      <Typography variant="body_small" color="text.secondary">
        {t("scheduled_calls_table.no_calls")}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {calls.map((call: AppointmentV1) => (
        <Stack key={call.id} direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="subtitle1">
            {`${format(parseISO(call.start_time), "p")} – ${format(parseISO(call.end_time), "p")}`}
          </Typography>

          <Link to={`/messages/patients/${call.patient_id}`} style={{ textDecoration: "none" }}>
            <Typography variant="subtitle2" color="primary">
              {call.patient_first_name} {call.patient_last_name}
            </Typography>
          </Link>

          <FontAwesomeIcon icon={call.medium === "phone" ? faPhone : faVideo} fontSize="16px" color={BLUE[100]} />
        </Stack>
      ))}
    </Stack>
  );
};
